import { CircleQuestion } from 'app/assets/icons'
import {
    ResponsiveContainer,
    breakpoints,
    media,
    style
} from 'app/assets/styles'
import { CouponsSlider } from 'app/common/coupons-slider'
import { SplitLayout } from 'app/common/layout/split'
import { Loader } from 'app/common/loader'
import { H1, Text } from 'app/common/typography'
import { Layout } from 'app/ecommerce/common'
import { ErrorBanner } from 'app/ecommerce/common/components'
import { SelectionScreen } from 'app/ecommerce/common/components/discounts'
import { Points } from 'app/kingdom/points'
import { FormCoupon } from 'app/kingdom/privileges'
import { KingdomTutorialModal } from 'app/kingdom/tutorials'
import React, { Component, Fragment } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import Modal from 'react-modal'
import { colors } from 'shared/assets/styles'
import type { MapById, Product } from 'shared/store/ecommerce/catalog'
import type { ItemInfos, ProcessCoupon } from 'shared/store/ecommerce/order'
import type { EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata/events-definitions'
import type {
    CacPromotion,
    ClickAndCollectPromotion,
    Coupon as CouponType,
    SelectionScreenInfos
} from 'shared/types'
import {
    catalogType,
    couponOrigin,
    featureCodes,
    promoType,
    promotionOriginType
} from 'shared/utils'
import { getPromotionValue } from 'shared/utils/ecommerce/utils'
import styled from 'styled-components'

const { MENU, PRODUCT } = catalogType
const { tertiary } = colors
const { medium, xsmallLegacy } = breakpoints
const { classicModalMobileStyle, classicModalStyle } = style
const { COMMON } = promotionOriginType
const { KINGDOM_POINTS, OPERATION_REPEAT_COUPON } = couponOrigin

type Props = {
    featuresAvailable: Array<string>,
    coupons: Array<CouponType>,
    nbCoupons: number,
    kingdomDataLoaded: boolean,
    promotionsById: MapById<CacPromotion>,
    processCouponNavigation: (
        coupon: CouponType,
        promotion: CacPromotion
    ) => ProcessCoupon,
    getProduct: (id: string) => ?Product,
    fetchPrivilegesData: () => void,
    fetchPointsData: () => void,
    setCurrentItemInfos: (productInfos: ItemInfos) => void,
    navigate: (to: string, state?: any) => void,
    sendMeta: EventMetadata => void,
    intl: IntlShape
}

type State = {
    couponSelected?: CouponType,
    couponIndex: ?number,
    displaySelectionScreen: boolean,
    selectionScreenInfos?: SelectionScreenInfos,
    displayCrownsScreen: boolean,
    width: number,
    error?: string,
    shouldShowTuto?: boolean,
    loading: boolean
}

class Discounts extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            couponSelected: undefined,
            couponIndex: null,
            displaySelectionScreen: false,
            selectionScreenInfos: undefined,
            displayCrownsScreen: false,
            width: window.innerWidth,
            error: undefined,
            shouldShowTuto: false,
            loading: false
        }
    }

    componentDidMount() {
        const { fetchPrivilegesData, fetchPointsData } = this.props
        fetchPrivilegesData()
        fetchPointsData()
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions)
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    sendMeta = (promotion: ClickAndCollectPromotion) => {
        const { getProduct, sendMeta } = this.props
        const { couponSelected } = this.state
        const { couponCode } = promotion
        if (couponSelected) {
            let coupon_value = getPromotionValue(promotion)
            if (!coupon_value) {
                const product = couponCode && getProduct(couponCode)
                coupon_value = (product && product.price) || ''
            }
            sendMeta({
                ...eventsDefinitions.ECOMM_COUPON,
                event_label: couponSelected.label
            })
            sendMeta({
                ...eventsDefinitions.GA4_ECOMM_PROMOTION,
                coupon: couponSelected.label,
                coupon_id: couponSelected.promotionId,
                coupon_value
            })
        }
    }

    openCouponModal = (couponIndex: number) => {
        this.setState({ couponIndex })
    }

    closeCouponModal = () => this.setState({ couponIndex: null })

    setSelectionScreen = (selectionScreenInfos: SelectionScreenInfos) => {
        this.setState({
            displaySelectionScreen: true,
            selectionScreenInfos
        })
    }

    closeSelectionScreen = () => {
        this.setState({
            selectionScreenInfos: undefined,
            displaySelectionScreen: false
        })
    }

    showTutorial = () => {
        const { shouldShowTuto } = this.state
        this.setState({ shouldShowTuto: !shouldShowTuto })
    }

    renderSelectionScreenModal = () => {
        const { setCurrentItemInfos } = this.props
        const {
            selectionScreenInfos,
            width,
            displaySelectionScreen
        } = this.state
        return (
            <Modal
                isOpen={displaySelectionScreen}
                //$FlowFixMe
                onRequestClose={this.closeSelectionScreen}
                style={
                    width < medium ? classicModalMobileStyle : classicModalStyle
                }>
                {selectionScreenInfos && (
                    <SelectionScreen
                        selectionScreenInfos={selectionScreenInfos}
                        handleClose={this.closeSelectionScreen}
                        setCurrentItemInfos={setCurrentItemInfos}
                    />
                )}
            </Modal>
        )
    }

    clearError = () => this.setState({ error: undefined })

    renderTitle = () => {
        return (
            <TitleContainer>
                <FormattedMessage id="clickandcollect.discounts.kingdom.title">
                    {(message: string) => <StyledH1 center>{message}</StyledH1>}
                </FormattedMessage>
                <div onClick={this.showTutorial}>
                    <CircleQuestion />
                </div>
            </TitleContainer>
        )
    }

    renderCoupons = () => {
        const { coupons } = this.props
        return coupons && coupons.length > 0 ? (
            <CouponsContainer>
                <CouponsSlider
                    coupons={coupons}
                    onClick={(coupon?: CouponType) => {
                        this.setState({ couponSelected: coupon })
                        coupon && this.validateCoupon(coupon)
                    }}
                    fromEcommerce
                />
            </CouponsContainer>
        ) : (
            <NoPrivilegesContainer>
                <FormattedMessage id="kingdom.no.privileges.title">
                    {(text: string) => <Text>{text}</Text>}
                </FormattedMessage>
            </NoPrivilegesContainer>
        )
    }

    validateCoupon = async (coupon: CouponType) => {
        const { id, promotionId, origin, category } = coupon
        const {
            promotionsById,
            processCouponNavigation,
            navigate,
            intl
        } = this.props
        const promotion = promotionsById[promotionId || '']
        let route, errorMessage

        if (promotion) {
            const CCpromo = {
                couponCode: id,
                idReboot: promotion.idReboot,
                promoType: promotion.promoType,
                promoValue: promotion.promoValue || 0,
                promotionId,
                promotionName: promotion.name,
                threshold: promotion.threshold,
                isCumulative: coupon.isCumulative,
                operationCode: coupon.operationCode
            }

            const promotionOrigin =
                origin === OPERATION_REPEAT_COUPON
                    ? OPERATION_REPEAT_COUPON
                    : origin === KINGDOM_POINTS
                    ? undefined
                    : COMMON

            const {
                listItems,
                type,
                error,
                hasOptions,
                listRequiredItems,
                requiredType
            } = await processCouponNavigation(coupon, promotion)
            errorMessage = error

            if (listRequiredItems && requiredType && !errorMessage) {
                this.setSelectionScreen({
                    listItems: listRequiredItems,
                    type: requiredType,
                    promotionOrigin,
                    promotionToApply: CCpromo,
                    coupon
                })
                this.setState({ loading: false })
                return
            }

            if (listItems && type && !errorMessage) {
                this.setSelectionScreen({
                    listItems,
                    promotion: CCpromo,
                    type,
                    promotionOrigin
                })
            } else {
                switch (type) {
                    case MENU:
                        route = 'routes.clickandcollect.menu'
                        break
                    case PRODUCT:
                        route = hasOptions
                            ? 'routes.clickandcollect.product'
                            : 'routes.clickandcollect.catalog'
                        break
                    case promoType.ORDER:
                        route = 'routes.clickandcollect.cart.summary'
                        break
                    default:
                        break
                }

                if (route && !errorMessage) {
                    this.sendMeta(CCpromo)
                    navigate(
                        intl.formatMessage({
                            id: route
                        }),
                        {
                            promotion: CCpromo,
                            promotionOrigin
                        }
                    )
                } else if (!errorMessage) {
                    errorMessage = 'error.section.title'
                }
            }
        } else {
            errorMessage = 'error.section.title'
        }
        this.setState({ error: errorMessage })
    }

    renderKingdom = () => {
        const { featuresAvailable, nbCoupons } = this.props
        const kingdomPoints = featuresAvailable.includes(
            featureCodes.KINGDOM_POINTS
        )
        return kingdomPoints ? (
            <SplitLayout
                noStyle
                className="ecomm-kingdom"
                activeLeft={true}
                leftBlock={this.renderKingdomLeftBlock()}
                idTitleLeft={'kingdom.dashboard.points.title'}
                rightBlock={this.renderKingdomRightBlock()}
                idTitleRight="kingdom.dashboard.privileges.title"
                valuesTitleRight={{ number: nbCoupons }}
            />
        ) : (
            this.renderCoupons()
        )
    }

    renderKingdomLeftBlock = () => {
        return (
            <Points
                fromEcommerce
                openSelectionScreen={(
                    selectionScreenInfos: SelectionScreenInfos,
                    error
                ) => {
                    const { listItems } = selectionScreenInfos
                    if (listItems && !error) {
                        const skipProductPurchaseScreen = !listItems.find(
                            item =>
                                item.hasIce ||
                                item.options.length > 0 ||
                                item.hasPickUpLater
                        )
                        this.setSelectionScreen({
                            ...selectionScreenInfos,
                            isKingdomPoints: skipProductPurchaseScreen
                        })
                    }
                    error && this.setState({ error })
                }}
                loadingPointsTransformation={loading =>
                    this.setState({ loading })
                }
            />
        )
    }

    renderKingdomRightBlock = () => {
        return (
            <Fragment>
                {this.renderCoupons()}
                <FormCoupon titleKey="kingdom.privileges.form.title" />
            </Fragment>
        )
    }

    render() {
        const { intl, featuresAvailable } = this.props
        const { error, shouldShowTuto, width, loading } = this.state
        const isActiveNewKingdomAssets = featuresAvailable.includes(
            featureCodes.ENABLE_NEW_KINGDOM_ASSETS
        )
        const mobile = width < xsmallLegacy - 1
        return (
            <Layout
                border
                backEnabled
                background="plain"
                backgroundColor={tertiary}>
                {this.renderSelectionScreenModal()}
                {error && (
                    <ErrorBanner
                        sticky
                        text={intl.formatMessage({ id: error })}
                        onClose={this.clearError}
                    />
                )}
                <StyledContainer>
                    <MainContainer>
                        {this.renderTitle()}
                        <StyledLoader loading={loading}>
                            {this.renderKingdom()}
                        </StyledLoader>
                        <Modal
                            isOpen={shouldShowTuto}
                            ariaHideApp={false}
                            style={
                                mobile
                                    ? style.classicModalMobileStyle
                                    : style.classicModalStyle
                            }>
                            <KingdomTutorialModal
                                handleClose={this.showTutorial}
                                isActiveNewKingdomAssets={
                                    isActiveNewKingdomAssets
                                }
                            />
                        </Modal>
                    </MainContainer>
                </StyledContainer>
            </Layout>
        )
    }
}

const StyledLoader = styled(Loader)`
    flex: 1;
    min-height: ${window.innerHeight - 180}px;
    ${media.medium`
        min-height: ${window.innerHeight - 230}px;
    `}
`

const StyledH1 = styled(H1)`
    margin: 20px 0 30px;
    ${media.medium`
        margin: 20px 20px 30px 0;
    `};
`

const TitleContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`

const StyledContainer = styled(ResponsiveContainer)`
    margin-top: 90px;
    height: calc(100vh - 90px);
    ${media.medium`
        margin-top: 150px;
        height: calc(100vh - 150px);
    `};
`

const MainContainer = styled.div`
    padding-bottom: 50px;
`

const CouponsContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 30px;
    background-color: ${tertiary};
`

const NoPrivilegesContainer = styled.div`
    width: 100%;
    margin: 20px 0 30px;
    text-align: center;
`

const DiscountsComponent = injectIntl(Discounts)
export { DiscountsComponent }
